import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { AuditItem } from '../../data/types';

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingVertical: 30,
        paddingHorizontal: 30,
        fontFamily: 'Times-Roman',
    },
    title: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 11,
        textAlign: 'center',
        color: '#555',
        marginBottom: 12,
    },
    generatedOn: {
        fontSize: 9,
        textAlign: 'center',
        color: '#888',
        marginBottom: 12,
    },
    table: {
        flexDirection: 'column',
        borderTop: 1,
        borderColor: '#bbb',
    },
    headerRow: {
        flexDirection: 'row',
        borderBottom: 1,
        borderColor: '#bbb',
        paddingVertical: 4,
        backgroundColor: '#f2f2f2',
    },
    row: {
        flexDirection: 'row',
        borderBottom: 1,
        borderColor: '#e5e5e5',
        paddingVertical: 4,
    },
    cell: {
        fontSize: 9,
        paddingHorizontal: 3,
    },
    whenCell: { width: '16%' },
    actorCell: { width: '18%' },
    actionCell: { width: '14%' },
    objectCell: { width: '14%' },
    detailCell: { width: '38%' },
    headerCell: {
        fontSize: 9,
        fontWeight: 'bold',
        paddingHorizontal: 3,
    },
    empty: {
        fontSize: 11,
        textAlign: 'center',
        marginTop: 20,
        color: '#666',
    },
    pageNumber: {
        position: 'absolute',
        bottom: 15,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 9,
        color: '#888',
    },
});

const formatTimestamp = (ts?: Date) => {
    if (!ts) return '';
    const d = ts instanceof Date ? ts : new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString();
};

export type AuditHistoryDocProps = {
    title: string;
    subtitle?: string;
    items: AuditItem[];
    getUserName: (userId: string) => string;
};

export const AuditHistoryDoc = (props: AuditHistoryDocProps) => {
    const { title, subtitle, items, getUserName } = props;

    return (
        <Document>
            <Page size='LETTER' style={styles.page} wrap>
                <Text style={styles.title}>{title}</Text>
                {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
                <Text style={styles.generatedOn}>Generated {new Date().toLocaleString()}</Text>

                {items.length === 0 ? (
                    <Text style={styles.empty}>No audit history available.</Text>
                ) : (
                    <View style={styles.table}>
                        <View style={styles.headerRow} fixed>
                            <Text style={[styles.headerCell, styles.whenCell]}>When</Text>
                            <Text style={[styles.headerCell, styles.actorCell]}>Actor</Text>
                            <Text style={[styles.headerCell, styles.actionCell]}>Action</Text>
                            <Text style={[styles.headerCell, styles.objectCell]}>Object</Text>
                            <Text style={[styles.headerCell, styles.detailCell]}>Detail</Text>
                        </View>
                        {items.map((item, idx) => {
                            const actorText = `${getUserName(item.actor?.userId ?? '')}${item.actor?.role ? ` - ${item.actor.role}` : ''}`;
                            return (
                                <View
                                    key={`${item.itemId ?? 'audit'}-${idx}`}
                                    style={styles.row}
                                    wrap={false}
                                >
                                    <Text style={[styles.cell, styles.whenCell]}>
                                        {formatTimestamp(item.timestamp)}
                                    </Text>
                                    <Text style={[styles.cell, styles.actorCell]}>{actorText}</Text>
                                    <Text style={[styles.cell, styles.actionCell]}>{item.action ?? ''}</Text>
                                    <Text style={[styles.cell, styles.objectCell]}>
                                        {item.detail?.objectType ?? ''}
                                    </Text>
                                    <Text style={[styles.cell, styles.detailCell]}>
                                        {item.detail?.description ?? ''}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                )}

                <Text
                    style={styles.pageNumber}
                    render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
                    fixed
                />
            </Page>
        </Document>
    );
};
